'use client'

import {FC, PropsWithChildren} from 'react'

import {Experiment} from '@/components/common/ab-testing/Experiment'
import {Variant} from '@/components/common/ab-testing/Variant'
import {LanguageWarningModal} from '@/components/common/modal/LanguageWarningModal'
import {SnackbarProvider} from '@/components/common/snackbar/context/SnackbarContext'
import {ABTestProvider} from '@/components/context/ABTest'
import {MobileProvider} from '@/components/context/MobileProvider'
import {PreferencesAnalytics} from '@/components/context/PreferencesAnalytics'
import {ProfilePreferencesProvider} from '@/components/context/ProfilePreferences'
import {UserDataProvider} from '@/components/context/UserData'
import {HelpCenterProvider} from '@/components/help-center/context/HelpCenterContext'
import {LanguageSuggestion} from '@/components/i18n/LanguageSuggestion'
import {ABTestNames} from '@/utils/constants'
import {CookieRecord} from '@/utils/preferences/types'
import {Prices, PricesV2} from '@/utils/pricing'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {useMatchMedia} from '@/utils/ui/hooks/useMatchMedia'

import {PricesProvider} from '../../components/PricesProvider'
import {BrowserPreferences, RequestPreferences} from './ProviderVariants'

/**
 * Everything the shell can only learn from a request, including the prices that need a database.
 * Passed as one object so the fields cannot disagree with each other about which mode we are in.
 */
type RequestScopedValues = {
  cookies: CookieRecord
  // Resolved from x-forwarded-host / host in the server layout.
  host: string
  // The stored country, else Cloudflare's geolocation of this request
  country?: string
  isMobile: boolean
  // A database read, so only a layout that renders per request can supply it.
  pricesV2: PricesV2
}

type RequestScopedProvidersProps = {
  /**
   * `null` means the surrounding page is prerendered — there was no request to read, so these
   * values are resolved in the browser instead. Anything else means a per-request render.
   */
  request: RequestScopedValues | null
  // A file read, so it is available in both modes.
  prices: Prices | null
}

/**
 * Everything whose value depends on the request, plus the providers that render consumers of it.
 * This is the half of the shell a statically rendered route sources differently, and the only half
 * that remounts when navigating between a static and a dynamic page — `SharedProviders` holds the
 * rest, above the seam.
 */
export const RequestScopedProviders: FC<PropsWithChildren<RequestScopedProvidersProps>> = ({
  request,
  prices,
  children,
}) => {
  const isStatic = request === null

  const Preferences = isStatic ? BrowserPreferences : RequestPreferences

  // The variants ignore these under static rendering; the empty defaults are only here so both
  // branches share one call site.
  const {cookies = {}, host = '', country, pricesV2} = request ?? {}

  // No user agent to read under static rendering, so match the `mobile || tablet` contract of
  // useMobile() against the viewport instead. `false` until resolved after hydration.
  const isBrowserMobile = useMatchMedia(TABLET_SCREENSIZE)
  const isMobile = request?.isMobile ?? isBrowserMobile ?? false

  return (
    <ABTestProvider>
      <Preferences host={host} cookies={cookies} country={country}>
        <PreferencesAnalytics />
        <PricesProvider prices={prices} pricesV2={pricesV2}>
          <UserDataProvider>
            <MobileProvider isMobile={isMobile}>
              <SnackbarProvider>
                <HelpCenterProvider>
                  <ProfilePreferencesProvider>{children}</ProfilePreferencesProvider>
                  <LanguageWarningModal />
                  <LanguageSuggestion />
                  <Experiment name={ABTestNames.sanityCheck}>
                    <Variant name="A" />
                    <Variant name="B" />
                  </Experiment>
                </HelpCenterProvider>
              </SnackbarProvider>
            </MobileProvider>
          </UserDataProvider>
        </PricesProvider>
      </Preferences>
    </ABTestProvider>
  )
}
